import { MACHINE_TYPES } from '../../shared/constants';
import { VendingMachineTypeId } from '../../shared/types';
import {
    CarWashIcon,
    CigaretteIcon,
    CoffeeIcon,
    HeartIcon,
    IceCreamIcon,
    JoystickIcon,
    JuiceIcon,
    LoyaltyIconComponent,
    PopcornIcon,
    SnackIcon,
    WaterDropIcon,
} from './components/icons';

export const DEFAULT_LOYALTY_BALANCE_FOR_PRIZE = 0;
export const DEFAULT_DISABLED_LOYALTY_PROGRAM_BALANCE = 0;
export const DEFAULT_LOYALTY_PRODUCT_MIN_PRICE = 0;

/**
 * How long the loyalty banner must stay on screen before it counts as seen
 * (SWRN-1747). A render that gets hidden again on the next commit — payment
 * buttons appearing in the same tick as the loyalty fetch resolves — must not
 * burn the one-time flag.
 */
export const LOYALTY_BANNER_SEEN_DELAY_MS = 2000;

/**
 * Icon shown in loyalty progress cells and the balance badge, per machine type (SWRN-1739).
 */
export const LOYALTY_ICON_BY_MACHINE_TYPE: Record<
    VendingMachineTypeId,
    LoyaltyIconComponent
> = {
    [MACHINE_TYPES.SNACK_MACHINE]: SnackIcon,
    [MACHINE_TYPES.COFFEE_AUTOMAT]: CoffeeIcon,
    [MACHINE_TYPES.SNACK_MACHINE_SPARKA]: SnackIcon,
    [MACHINE_TYPES.COFFEE_MACHINE_SPARKA]: CoffeeIcon,
    [MACHINE_TYPES.COFFEE_MACHINE]: CoffeeIcon,
    [MACHINE_TYPES.FRESH_MACHINE]: JuiceIcon,
    [MACHINE_TYPES.ICE_CREAM_MACHINE]: IceCreamIcon,
    [MACHINE_TYPES.CIGARETTE_MACHINE]: CigaretteIcon,
    [MACHINE_TYPES.CONDOM_MACHINE]: HeartIcon,
    [MACHINE_TYPES.FRIDGE]: SnackIcon,
    [MACHINE_TYPES.WATER_MACHINE_PULS]: WaterDropIcon,
    [MACHINE_TYPES.SNACK_MACHINE_PULS]: SnackIcon,
    [MACHINE_TYPES.FRIDGE_SMART]: SnackIcon,
    [MACHINE_TYPES.PULSE_MACHINE]: JoystickIcon,
    [MACHINE_TYPES.CAR_WASH_MACHINE]: CarWashIcon,
    [MACHINE_TYPES.POPCORN_PULSE_MACHINE]: PopcornIcon,
};

/** Falls back to the coffee cup for unknown/missing machine types. */
export const getLoyaltyIconByMachineType = (
    typeId: VendingMachineTypeId | undefined,
): LoyaltyIconComponent =>
    (typeId && LOYALTY_ICON_BY_MACHINE_TYPE[typeId]) || CoffeeIcon;

/**
 * The item noun used in the loyalty banner copy, varied per machine type
 * (SWRN-1747). Each value maps to a `loyalty.banner.body.<noun>` i18n key,
 * so the whole phrase (including gender agreement) is translated per noun.
 */
export type LoyaltyBannerNoun =
    | 'snack'
    | 'drink'
    | 'water'
    | 'icecream'
    | 'product'
    | 'game'
    | 'carwash'
    | 'popcorn';

export const LOYALTY_NOUN_BY_MACHINE_TYPE: Record<
    VendingMachineTypeId,
    LoyaltyBannerNoun
> = {
    [MACHINE_TYPES.SNACK_MACHINE]: 'snack',
    [MACHINE_TYPES.COFFEE_AUTOMAT]: 'drink',
    [MACHINE_TYPES.SNACK_MACHINE_SPARKA]: 'snack',
    [MACHINE_TYPES.COFFEE_MACHINE_SPARKA]: 'drink',
    [MACHINE_TYPES.COFFEE_MACHINE]: 'drink',
    [MACHINE_TYPES.FRESH_MACHINE]: 'drink',
    [MACHINE_TYPES.ICE_CREAM_MACHINE]: 'icecream',
    [MACHINE_TYPES.CIGARETTE_MACHINE]: 'product',
    [MACHINE_TYPES.CONDOM_MACHINE]: 'product',
    [MACHINE_TYPES.FRIDGE]: 'product',
    [MACHINE_TYPES.WATER_MACHINE_PULS]: 'water',
    [MACHINE_TYPES.SNACK_MACHINE_PULS]: 'snack',
    [MACHINE_TYPES.FRIDGE_SMART]: 'product',
    [MACHINE_TYPES.PULSE_MACHINE]: 'game',
    [MACHINE_TYPES.CAR_WASH_MACHINE]: 'carwash',
    [MACHINE_TYPES.POPCORN_PULSE_MACHINE]: 'popcorn',
};

/** Falls back to the generic "product" noun for unknown/missing machine types. */
export const getLoyaltyNounByMachineType = (
    typeId: VendingMachineTypeId | undefined,
): LoyaltyBannerNoun =>
    (typeId && LOYALTY_NOUN_BY_MACHINE_TYPE[typeId]) || 'product';
